import React from "react";
import PropTypes from "prop-types";
import {
  Typography,
  Button,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";

import { useTranslation } from "react-i18next";
import MaskSelectorToolStyles from "./MaskSelectorToolStyles";

import scanSplash from "./img/scan_splash.svg";

import BrowserNotSupported from "./BrowserNotSupported";

const StartPage = ({ handleNext }) => {
  const { classes } = MaskSelectorToolStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation("pages.MaskSelectorTool.StartPage");

  // Localized Strings
  const title = t("title");
  const missionDescription = t("missionDescription");
  const purposeDescription = t("purposeDescription");
  const whatWeAskingTitle = t("whatWeAskingTitle");
  const sleepStudyRequest = t("sleepStudyRequest");
  const photosRequest = t("photosRequest");
  const stepsTitle = t("stepsTitle");
  const step1 = t("step1");
  const step2 = t("step2");
  const step3 = t("step3");
  const startButton = t("startButton");

  return (
    <>
      <BrowserNotSupported />

      <Box className={classes.centeredBox}>
        <img
          className={classes.responsive_image_40_percent_height}
          src={scanSplash}
          alt=""
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          padding: "20px",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          mb: 3,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontSize: "24px",
            fontWeight: 700,
            color: "text.primary",
            mb: 2,
            textAlign: "center",
          }}
          data-testid="start-page-title"
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            mb: 2,
            lineHeight: 1.6,
            color: "text.primary",
          }}
          data-testid="start-page-description"
        >
          {missionDescription}
        </Typography>

        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            mb: 3,
            lineHeight: 1.6,
            color: "text.primary",
          }}
        >
          {purposeDescription}
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            mb: 2,
            color: "text.primary",
          }}
          data-testid="what-we-asking-title"
        >
          {whatWeAskingTitle}
        </Typography>

        <List>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <CheckCircleOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={sleepStudyRequest}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
              }}
            />
          </ListItem>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <CheckCircleOutlineIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={photosRequest}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
              }}
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            mb: 2,
            color: "text.primary",
          }}
          data-testid="steps-title"
        >
          {stepsTitle}
        </Typography>

        <List>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <LooksOneIcon color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary={step1}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
              }}
            />
          </ListItem>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <LooksTwoIcon color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary={step2}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
              }}
            />
          </ListItem>
          <ListItem sx={{ pl: 0 }}>
            <ListItemIcon>
              <Looks3Icon color="secondary" />
            </ListItemIcon>
            <ListItemText
              primary={step3}
              primaryTypographyProps={{
                fontWeight: 500,
                color: "text.primary",
              }}
            />
          </ListItem>
        </List>
      </Paper>

      <Box
        sx={{
          position: "relative",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          mt: isMobile || isTablet ? 12 : 0,
        }}
      >
        <Button
          disableRipple
          onClick={handleNext}
          variant="contained"
          color="secondary"
          size="large"
          data-testid="start-page-start-button"
          className={classes.single_button}
        >
          {startButton}
        </Button>
      </Box>
    </>
  );
};

StartPage.propTypes = {
  handleNext: PropTypes.func.isRequired,
};

export default StartPage;
